// Общие DOM/маркер-хелперы фазы 2 разбиения MapView.vue - используются
// несколькими composable'ами (узлы, курсор маршрута, GPS-маркер, POI-пины,
// маркеры маршрута) без явного единственного владельца, поэтому вынесены
// сюда, а не дублированы/привязаны к одному из composable'ов. Чистые функции
// без Vue-реактивности - принимают map/marker явным параметром, ничего не
// замыкают.
import * as maplibregl from "maplibre-gl";
import type { Marker, Popup } from "maplibre-gl";
import type { Feature } from "geojson";
import type { MapCtx } from "./mapContext";
import { DETAIL_MARKER_MIN_ZOOM } from "./constants";

// Общий сеттер для GeoJSON-источников карты (линия маршрута, POI-кластер и
// т.д.) - используется и route-специфичным кодом (useRouteDrawing.ts), и
// пока ещё не вынесенным кодом POI-кластера в MapView.vue.
export const setGeoJsonSourceData = (
  map: maplibregl.Map | null,
  id: string,
  features: Feature[]
): void => {
  const source = map?.getSource(id) as maplibregl.GeoJSONSource | undefined;
  source?.setData({ type: "FeatureCollection", features });
};

export const escapeHtml = (value: string | number | null | undefined) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");

// MapLibre-маркеры принимают настоящий DOM-элемент, а не html-строку, как
// divIcon у Leaflet, поэтому здесь та же разметка собирается в отсоединённый
// div (класс - бывший className у divIcon, старые CSS-правила подходят как есть).
//
// `size` заменяет divIcon-овский iconSize (Leaflet выставлял inline
// width/height) - без него .route-map-pin (width/height 100%) не имеет
// коробки под себя, и SVG раздувается до своего натурального размера.
export const markerElement = (
  className: string,
  html: string,
  size?: [number, number]
): HTMLDivElement => {
  const el = document.createElement("div");
  el.className = className;
  if (size) {
    el.style.width = `${size[0]}px`;
    el.style.height = `${size[1]}px`;
  }
  el.innerHTML = html;
  return el;
};

// Бывший iconSize у divIcon для пинов-капель (узлы + бейджи маршрута).
export const PIN_SIZE: [number, number] = [36, 48];

// Тот же векторный пин-капля, что и у маркера узла, окрашен по варианту, в
// круглой голове глиф или плоская иконка (фон уже вырезан,
// frontend/src/assets/poi-icons/) вместо обрезанного фото. Вынесено из
// createRouteBadgeElement отдельной функцией, возвращающей только html пина
// (без обёртки markerElement), чтобы вызывающая сторона могла и создать
// маркер, и позже обновить его innerHTML на месте (см.
// composables/useRouteEditor.ts::syncWaypointMarkers - тип/имя вейпоинта
// меняются без пересоздания maplibregl.Marker).
export const routeMapPinHtml = (
  variant: string,
  glyph: string,
  label: string,
  icon?: string | null
) => `
  <div
    class="route-map-pin route-map-pin--${escapeHtml(variant)}"
    title="${escapeHtml(label)}"
    aria-label="${escapeHtml(label)}"
  >
    <svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path class="route-map-pin__body" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z" />
      <circle class="route-map-pin__hole" cx="192" cy="192" r="144" />
    </svg>
    ${icon
      ? `<img class="route-map-pin__icon" src="${escapeHtml(icon)}" alt="" />`
      : `<span class="route-map-pin__glyph">${escapeHtml(glyph)}</span>`}
  </div>
`;

export const createRouteBadgeElement = (
  variant: string,
  glyph: string,
  label: string,
  icon?: string | null
) => markerElement("route-map-marker-icon", routeMapPinHtml(variant, glyph, label, icon), PIN_SIZE);

// Начало и конец маршрута из каталога: зелёная точка и клетчатый флажок
// вместо одинаковых капель «A»/«B» - читается как «отсюда - сюда» без
// расшифровки и не занимает столько места на карте. Класс тот же
// (`route-map-marker-icon`), что у капель: на нём держится скрытие маркеров
// на мелком зуме.
export const START_DOT_SIZE: [number, number] = [18, 18];
export const FINISH_FLAG_SIZE: [number, number] = [26, 30];

export const createRouteStartDotElement = (label: string) =>
  markerElement(
    "route-map-marker-icon",
    `<div class="route-start-dot" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}"></div>`,
    START_DOT_SIZE
  );

export const createRouteFinishFlagElement = (label: string) =>
  markerElement(
    "route-map-marker-icon",
    `<div class="route-finish-flag" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">
      <svg viewBox="0 0 26 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <line x1="3" y1="2" x2="3" y2="29" class="route-finish-flag__pole" />
        <rect x="3" y="2" width="20" height="14" class="route-finish-flag__bg" />
        <path class="route-finish-flag__check" d="M3 2h5v5H3zM13 2h5v5h-5zM8 7h5v5H8zM18 7h5v5h-5zM3 12h5v4H3zM13 12h5v4h-5z" />
      </svg>
    </div>`,
    FINISH_FLAG_SIZE
  );

// Пины 36x48, якорь в носике; попап должен уходить выше всего пина. Оба
// числа перенесены из Leaflet-овских iconAnchor/popupAnchor.
export const PIN_POPUP_OFFSET: [number, number] = [0, -46];

// У MapLibre-маркеров нет bindPopup - Popup создаётся и привязывается явно.
// Общий для узлов/POI-пинов/маркеров маршрута хелпер: если попап уже есть,
// просто меняет HTML (без пересоздания и потери подписки на 'open'),
// иначе создаёт новый и опционально вешает ленивый onOpen (погода/станция).
//
// Гвардия клика ставится ЗДЕСЬ, а не в markerElement/createNodeElement и т.п.,
// потому что это единственная точка, через которую проходят именно пины с
// попапом (маркеры линейки popup не получают вовсе - им гвардия не нужна и не
// должна мешать перетаскиванию/удалению точек). Вешается до marker.addTo() у
// всех вызывающих сторон, поэтому срабатывает раньше внутреннего click-обработчика
// MapLibre (тот навешивается только в addTo()) - без этого клик по режиму
// линейки открывал бы попап под пином вместо/вместе с добавлением точки.
export const upsertPopupContent = (marker: Marker, html: string, ctx: MapCtx, onOpen?: () => void): void => {
  const existing = marker.getPopup();
  if (existing) {
    existing.setHTML(html);
    return;
  }
  const popup = new maplibregl.Popup({ offset: PIN_POPUP_OFFSET, maxWidth: "none" }).setHTML(html);
  if (onOpen) popup.on("open", onOpen);
  marker.setPopup(popup as Popup);
  marker.getElement().addEventListener(
    "click",
    (e) => {
      if (ctx.isRulerActive()) e.stopPropagation();
    },
    { capture: true }
  );
};

// У MapLibre нет членства в layer-group для добавления/удаления маркера,
// поэтому скрытие по зуму делается через element самого маркера.
export const setMarkerVisible = (marker: Marker, visible: boolean): void => {
  marker.getElement().style.display = visible ? "" : "none";
};

// Общий зум-гейт для мелких DOM-маркеров (узлы, POI-пины, точки маршрута) -
// ниже него слишком тесно/неинформативно, отдельные маркеры уступают
// векторным иконкам базовой карты.
export const shouldShowDetailMarkers = (map: maplibregl.Map | null): boolean => {
  const zoom = map?.getZoom();
  return typeof zoom === "number" && zoom >= DETAIL_MARKER_MIN_ZOOM;
};

export const formatMetric = (value: number | null | undefined, unit = "", digits = 1) =>
  value === null || value === undefined ? "–" : `${value.toFixed(digits)}${unit}`;

export const formatPercent = (value: number | null | undefined) =>
  value === null || value === undefined ? "–" : `${Math.round(value)}%`;

export const formatLocalDateTime = (isoString?: string) => {
  if (!isoString) return "–";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "–";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};
